/**
 * Centralized Invisible Character Analyzer
 * Provides a unified interface for detecting and analyzing invisible characters across all tools
 */

import { detectInvisibleChars, type DetectedCharacter } from './invisibleCharacters';
import { analyzeSecurityRisks, type SecurityAnalysis } from './securityAnalysis';

export interface InvisibleCharAnalysisOptions {
  includeSpaces?: boolean;
  includeSecurityAnalysis?: boolean;
  context?: string; // e.g., 'url', 'email-subject', 'email-body'
}

export interface InvisibleCharAnalysisResult {
  detectedChars: DetectedCharacter[];
  count: number;
  hasInvisibleChars: boolean;
  categorySummary: Map<string, number>;
  securityAnalysis?: SecurityAnalysis;
  severityLevel: 'none' | 'low' | 'medium' | 'high' | 'critical';
}

export interface InvisibleCharBulkAnalysisResult {
  label: string;
  text: string;
  analysis: InvisibleCharAnalysisResult;
}

/**
 * Analyze text for invisible characters with optional security analysis
 */
export function analyzeInvisibleCharacters(
  text: string,
  options: InvisibleCharAnalysisOptions = {}
): InvisibleCharAnalysisResult {
  const { includeSpaces = false, includeSecurityAnalysis = true } = options;
  
  const detectedChars = detectInvisibleChars(text, includeSpaces);
  const count = detectedChars.length;
  const hasInvisibleChars = count > 0;
  
  // Group by category
  const categorySummary = new Map<string, number>();
  detectedChars.forEach(char => {
    const current = categorySummary.get(char.character.category) || 0;
    categorySummary.set(char.character.category, current + 1);
  });
  
  // Security analysis if requested
  let securityAnalysis = undefined;
  let severityLevel: InvisibleCharAnalysisResult['severityLevel'] = 'none';
  
  if (hasInvisibleChars && includeSecurityAnalysis) {
    securityAnalysis = analyzeSecurityRisks(text, detectedChars);
    severityLevel = mapThreatLevel(securityAnalysis.threatLevel);
  }
  
  return {
    detectedChars,
    count,
    hasInvisibleChars,
    categorySummary,
    securityAnalysis,
    severityLevel,
  };
}

/**
 * Analyze multiple text sections in bulk
 */
export function analyzeBulkInvisibleCharacters(
  sections: Array<{ label: string; text: string }>,
  options: InvisibleCharAnalysisOptions = {}
): InvisibleCharBulkAnalysisResult[] {
  return sections.map(({ label, text }) => ({
    label,
    text,
    analysis: analyzeInvisibleCharacters(text, options),
  }));
}

/**
 * Get severity-based color for UI
 */
export function getSeverityColor(severity: InvisibleCharAnalysisResult['severityLevel']): 'red' | 'blue' | 'green' | 'grey' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'red';
    case 'medium':
      return 'blue';
    case 'low':
      return 'green';
    default:
      return 'grey';
  }
}

/**
 * Get category-based badge color
 */
export function getCategoryBadgeColor(category: string): 'green' | 'red' | 'blue' | 'grey' {
  switch (category) {
    case 'control':
      return 'green';
    case 'zero-width':
      return 'red';
    case 'directional':
    case 'space':
      return 'blue';
    default:
      return 'grey';
  }
}

/**
 * Format character summary for display
 */
export function formatCategorySummary(categorySummary: Map<string, number>): string {
  return Array.from(categorySummary.entries())
    .map(([cat, count]) => `${count} ${cat}`)
    .join(', ');
}

/**
 * Map threat level from security analysis to severity level
 */
function mapThreatLevel(level: string): InvisibleCharAnalysisResult['severityLevel'] {
  switch (level.toUpperCase()) {
    case 'CRITICAL': return 'critical';
    case 'HIGH': return 'high';
    case 'MEDIUM': return 'medium';
    case 'LOW': return 'low';
    default: return 'none';
  }
}

// Re-export commonly used types and functions for convenience
export { formatUnicodeCode, type InvisibleCharacter } from './invisibleCharacters';
export type { DetectedCharacter };

